import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Input,
  Label,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import { settingsService } from "../../service/settingsService";
import { useToast } from "../../contexts/ToastContext";

export default function RulesPage() {
  const [settings, setSettings] = useState([]);
  const [originalSettings, setOriginalSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { showSuccess } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getSettings();
      setSettings(data);
      setOriginalSettings(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, value) => {
    const updatedSettings = [...settings];

    console.log({index, value})

    updatedSettings[index].value = value;
    setSettings(updatedSettings);
  };

  const getInputType = (value) => {
    if (typeof value === "number") {
      return Number.isInteger(value) ? "number" : "number";
    }
    if (typeof value === "boolean") {
      return "checkbox";
    }
    return "text";
  };

  const getInputProps = (value, index) => {
    const inputType = getInputType(value);

    if (inputType === "checkbox") {
      return {
        type: "checkbox",
        checked: value,
        onChange: (e) => handleInputChange(index, e.target.checked),
      };
    }

    if (inputType === "number") {
      return {
        type: "number",
        value: value,
        onChange: (e) =>
          handleInputChange(index, parseFloat(e.target.value) || 0),
        step: Number.isInteger(value) ? 1 : 0.01,
      };
    }

    return {
      type: "text",
      value: value,
      onChange: (e) => handleInputChange(index, e.target.value),
    };
  };

  const handleUpdateSettings = async () => {
    try {
      setSaving(true);
      setErrorMessage(null);

      await settingsService.updateSettings(settings);
      
      showSuccess(
        "Settings have been updated and refreshed with the latest values from the server.",
        "Settings Updated Successfully!"
      );

      // Refresh settings from server to show updated values
      await fetchSettings();
    } catch (err) {
      console.error("Error updating settings:", err);
      console.error("Error response:", err.response);
      console.error("Error data:", err.response?.data);
      
      // Parse API error response
      let errorMessage = "Failed to update settings. Please try again.";
      
      // Check if we have a response with data
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        
        // Extract meaningful error message from API response
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message && errorData.message !== "error.http.500") {
          errorMessage = errorData.message;
        } else if (errorData.title && errorData.title !== "Internal Server Error") {
          errorMessage = errorData.title;
        }
      } 
      // Check if the error object itself has the API response structure
      else if (err.detail) {
        errorMessage = err.detail;
      } else if (err.title && err.title !== "Internal Server Error") {
        errorMessage = err.title;
      } else if (err.message && err.message !== "error.http.500") {
        errorMessage = err.message;
      }
      // Check for originalMessage from apiService
      else if (err.originalMessage && err.originalMessage !== "error.http.500") {
        errorMessage = err.originalMessage;
      }
      // Fallback to generic error message
      else if (err.message) {
        errorMessage = err.message;
      }
      
      setErrorMessage(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatKeyName = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleRestoreSettings = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings))); // Deep copy to restore
    setErrorMessage(null); // Clear any error messages
    showSuccess("Rules have been restored to their original values.", "Rules Restored");
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Row className="mt-4">
        <Col>
          <Card>
            <CardHeader className="bg-white">
              <h3 className="mb-0">System Rules</h3>
              <p className="text-muted mb-0">
                Configure system parameters and rules
              </p>
            </CardHeader>
            <CardBody>
              {errorMessage && (
                <Alert color="danger" className="mb-4">
                  <div className="d-flex align-items-center">
                    <i className="ni ni-notification-70 mr-2"></i>
                    <div>
                      <strong>Error!</strong> {errorMessage}
                    </div>
                  </div>
                </Alert>
              )}
              
              <Row>
                {settings.map((setting, index) => (
                  <Col md="6" lg="3" key={setting.key} className="mb-3">
                    <FormGroup>
                      <Label
                        for={`setting-${index}`}
                        className="form-control-label"
                      >
                        {formatKeyName(setting.key)}
                      </Label>
                      <Input
                        id={`setting-${index}`}
                        {...getInputProps(setting.value, index)}
                        size="sm"
                        className="form-control"
                      />
                    </FormGroup>
                  </Col>
                ))}
              </Row>

              <div className="mt-4 d-flex justify-content-end">
                <Button
                  color="secondary"
                  onClick={handleRestoreSettings}
                  disabled={saving}
                  className="px-4 mr-3"
                >
                  Restore rules
                </Button>
                <Button
                  color="primary"
                  onClick={handleUpdateSettings}
                  disabled={saving}
                  className="px-4"
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    "Update rules"
                  )}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
